import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { masterService } from "../../../api/masterService";
import { useNotification } from "../../../contexts/NotificationContext";
import { formatNumberWithSeparator, parseNumberFromSeparator } from "../../../utils/numberFormat";

export default function EditSellingPriceModal({ isOpen, onClose, priceItem, product, onSuccess }) {
  const { showSuccess, showError } = useNotification();
  const isEditing = !!priceItem;

  const [name, setName] = useState("");
  const [price, setPrice] = useState(0);
  const [status, setStatus] = useState("ACTIVE");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (priceItem) {
        setName(priceItem.name || "");
        setPrice(priceItem.price || 0);
        setStatus(priceItem.status || "ACTIVE");
      } else {
        setName("");
        setPrice(0);
        setStatus("ACTIVE");
      }
    }
  }, [isOpen, priceItem]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      showError("Nama harga wajib diisi");
      return;
    }
    if (Number(price) < 0) {
      showError("Harga jual tidak boleh bernilai negatif");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: name.trim(),
        price: Number(price),
        status: status === "ACTIVE" ? "ACTIVE" : "INACTIVE",
      };

      if (isEditing) {
        await masterService.products.prices.update(product.id, priceItem.id, payload);
        showSuccess("Varian harga jual berhasil diperbarui");
      } else {
        await masterService.products.prices.create(product.id, payload);
        showSuccess("Varian harga jual berhasil ditambahkan");
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || "Gagal menyimpan varian harga jual");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header Modal */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-teal-800 text-white">
          <div>
            <h3 className="text-lg font-bold text-white">
              {isEditing ? "Edit Harga Jual" : "Tambah Harga Jual"}
            </h3>
            <p className="text-xs text-teal-100/90 mt-0.5 font-medium">
              {product?.name || "Produk"}
            </p>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Card Info Harga Utama Produk */}
          <div className="p-4 bg-teal-50/70 border border-teal-100 rounded-2xl flex items-center justify-between">
            <div>
              <span className="text-[11px] font-semibold text-teal-700 uppercase tracking-wider block">
                Harga utama produk
              </span>
              <span className="text-base font-extrabold text-teal-950 mt-0.5 block">
                {formatCurrency(product?.sell_price || 0)}
              </span>
            </div>
            <span className="px-2.5 py-1 bg-white text-teal-700 text-xs font-bold rounded-lg border border-teal-200 shadow-2xs">
              Otomatis
            </span>
          </div>

          {/* Input Nama Harga */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Nama Harga <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Contoh: Grosir, Eceran, Member, Bengkel"
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-all"
            />
          </div>

          {/* Input Harga Jual Tambahan */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Harga Jual Tambahan <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400">
                Rp
              </span>
              <input
                type="text"
                required
                value={formatNumberWithSeparator(price)}
                onChange={(e) => setPrice(parseNumberFromSeparator(e.target.value))}
                placeholder="Masukkan harga jual"
                className="w-full pl-12 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-all"
              />
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5 font-medium">
              Harga tambahan menggunakan stok produk yang sama.
            </p>
          </div>

          {/* Toggle Switch: Aktifkan harga ini */}
          <div className="pt-2 flex items-center justify-between border-t border-slate-100">
            <span className="text-xs font-bold text-slate-700">
              Aktifkan harga ini
            </span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={status === "ACTIVE"}
                onChange={(e) => setStatus(e.target.checked ? "ACTIVE" : "INACTIVE")}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
            </label>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors cursor-pointer"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 rounded-xl bg-teal-600 text-white font-bold text-sm hover:bg-teal-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2 cursor-pointer"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Menyimpan...
                </>
              ) : (
                "Simpan Harga"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
